<template>
    <div class="accident-intake-wizard">
        <div v-if="isSubmitted" class="intake-submitted">
            <h2 class="intake-submitted__heading">{{ $t('pages.intake.success.heading') }}</h2>
            <p class="intake-submitted__message">{{ $t('pages.intake.success.message') }}</p>

            <div class="intake-submitted__actions">
                <common-button
                    class="intake-submitted__action"
                    :label="copyButtonLabel"
                    type="primary"
                    size="large"
                    :isSubmittable="false"
                    @click="copySummaryToClipboard"
                ></common-button>
                <common-button
                    class="intake-submitted__action"
                    :label="$t('pages.intake.success.download')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="downloadSummaryPdf"
                ></common-button>
            </div>

            <div class="intake-submitted__summary-wrap">
                <pre class="intake-submitted__summary">{{ submittedSummaryText }}</pre>
            </div>
        </div>

        <div v-else>
            <common-content-spacer height="small">
                <common-progress
                    :percentage="stepProgress"
                    :showSuccessIcon="false"
                    height="small"
                    color="secondary"
                ></common-progress>
                <p class="intake-page__step-label">{{ currentStepMeta.progress }}</p>
            </common-content-spacer>

            <div class="intake-page__step-intro">
                <common-page-header class="intake-page__step-heading" :heading="currentStepMeta.heading"></common-page-header>
                <p v-if="currentStepMeta.description" class="intake-page__step-description">
                    {{ currentStepMeta.description }}
                </p>
            </div>

            <accident-intake-about-you-form
                v-if="currentStep === 1"
                :initialData="formData.aboutYou"
                @sent="onStepComplete(1, $event)"
            ></accident-intake-about-you-form>

            <accident-intake-what-happened-form
                v-else-if="currentStep === 2"
                :initialData="formData.whatHappened"
                @back="onStepBack(2, $event)"
                @sent="onStepComplete(2, $event)"
            ></accident-intake-what-happened-form>

            <accident-intake-how-you-felt-form
                v-else-if="currentStep === 3"
                :initialData="formData.howYouFelt"
                @back="onStepBack(3, $event)"
                @sent="onStepComplete(3, $event)"
            ></accident-intake-how-you-felt-form>

            <accident-intake-symptoms-today-form
                v-else-if="currentStep === 4"
                :initialData="formData.symptomsToday"
                @back="onStepBack(4, $event)"
                @sent="onStepComplete(4, $event)"
            ></accident-intake-symptoms-today-form>

            <accident-intake-health-screening-form
                v-else-if="currentStep === 5"
                :initialData="formData.healthScreening"
                @back="onStepBack(5, $event)"
                @sent="onStepComplete(5, $event)"
            ></accident-intake-health-screening-form>

            <accident-intake-treatment-received-form
                v-else-if="currentStep === 6"
                :initialData="formData.treatmentReceived"
                @back="onStepBack(6, $event)"
                @sent="onStepComplete(6, $event)"
            ></accident-intake-treatment-received-form>

            <accident-intake-treating-providers-form
                v-else-if="currentStep === 7"
                :initialData="formData.treatingProviders"
                @back="onStepBack(7, $event)"
                @sent="onStepComplete(7, $event)"
            ></accident-intake-treating-providers-form>

            <accident-intake-tests-imaging-form
                v-else-if="currentStep === 8"
                :initialData="formData.testsImaging"
                @back="onStepBack(8, $event)"
                @sent="onStepComplete(8, $event)"
            ></accident-intake-tests-imaging-form>

            <accident-intake-previous-injuries-form
                v-else-if="currentStep === 9"
                :initialData="formData.previousInjuries"
                @back="onStepBack(9, $event)"
                @sent="onStepComplete(9, $event)"
            ></accident-intake-previous-injuries-form>

            <accident-intake-medical-surgical-history-form
                v-else-if="currentStep === 10"
                :initialData="formData.medicalSurgical"
                @back="onStepBack(10, $event)"
                @sent="onStepComplete(10, $event)"
            ></accident-intake-medical-surgical-history-form>

            <accident-intake-medications-allergies-form
                v-else-if="currentStep === 11"
                :initialData="formData.medicationsAllergies"
                @back="onStepBack(11, $event)"
                @sent="onStepComplete(11, $event)"
            ></accident-intake-medications-allergies-form>

            <accident-intake-work-information-form
                v-else-if="currentStep === 12"
                :initialData="formData.workInformation"
                @back="onStepBack(12, $event)"
                @sent="onStepComplete(12, $event)"
            ></accident-intake-work-information-form>

            <accident-intake-social-family-history-form
                v-else-if="currentStep === 13"
                :initialData="formData.socialFamily"
                @back="onStepBack(13, $event)"
                @sent="onStepComplete(13, $event)"
            ></accident-intake-social-family-history-form>

            <accident-intake-daily-activities-form
                v-else-if="currentStep === 14"
                :initialData="formData.dailyActivities"
                @back="onStepBack(14, $event)"
                @sent="onStepComplete(14, $event)"
            ></accident-intake-daily-activities-form>

            <accident-intake-health-questions-form
                v-else-if="currentStep === 15"
                :initialData="formData.healthQuestions"
                @back="onStepBack(15, $event)"
                @sent="onStepComplete(15, $event)"
            ></accident-intake-health-questions-form>

            <accident-intake-review-submit-form
                v-else-if="currentStep === 16"
                :initialData="formData.reviewSubmit"
                :intakeData="formData"
                @edit="onReviewEdit"
                @back="onStepBack(16, $event)"
                @sent="onStepComplete(16, $event)"
            ></accident-intake-review-submit-form>

            <accident-intake-confirm-submit-form
                v-else-if="currentStep === 17"
                :initialData="formData.confirmSubmit"
                :isSubmitting="isSubmitting"
                @back="onStepBack(17, $event)"
                @sent="onFinalSubmit"
            ></accident-intake-confirm-submit-form>

            <p v-if="submitError" class="intake-page__submit-error">{{ submitError }}</p>
        </div>
    </div>
</template>

<script>
    import AccidentIntakeAboutYouForm from 'components/forms/AccidentIntakeAboutYouForm'
    import AccidentIntakeWhatHappenedForm from 'components/forms/AccidentIntakeWhatHappenedForm'
    import AccidentIntakeHowYouFeltForm from 'components/forms/AccidentIntakeHowYouFeltForm'
    import AccidentIntakeSymptomsTodayForm from 'components/forms/AccidentIntakeSymptomsTodayForm'
    import AccidentIntakeHealthScreeningForm from 'components/forms/AccidentIntakeHealthScreeningForm'
    import AccidentIntakeTreatmentReceivedForm from 'components/forms/AccidentIntakeTreatmentReceivedForm'
    import AccidentIntakeTreatingProvidersForm from 'components/forms/AccidentIntakeTreatingProvidersForm'
    import AccidentIntakeTestsImagingForm from 'components/forms/AccidentIntakeTestsImagingForm'
    import AccidentIntakePreviousInjuriesForm from 'components/forms/AccidentIntakePreviousInjuriesForm'
    import AccidentIntakeMedicalSurgicalHistoryForm from 'components/forms/AccidentIntakeMedicalSurgicalHistoryForm'
    import AccidentIntakeMedicationsAllergiesForm from 'components/forms/AccidentIntakeMedicationsAllergiesForm'
    import AccidentIntakeWorkInformationForm from 'components/forms/AccidentIntakeWorkInformationForm'
    import AccidentIntakeSocialFamilyHistoryForm from 'components/forms/AccidentIntakeSocialFamilyHistoryForm'
    import AccidentIntakeDailyActivitiesForm from 'components/forms/AccidentIntakeDailyActivitiesForm'
    import AccidentIntakeHealthQuestionsForm from 'components/forms/AccidentIntakeHealthQuestionsForm'
    import AccidentIntakeReviewSubmitForm from 'components/forms/AccidentIntakeReviewSubmitForm'
    import AccidentIntakeConfirmSubmitForm from 'components/forms/AccidentIntakeConfirmSubmitForm'
    import IntakeAPI from 'api/IntakeAPI'
    import { buildIntakePayload } from '~/helpers/intakePayload'

    const TOTAL_STEPS = 17
    const LAST_IMPLEMENTED_STEP = 17
    const REVIEW_STEP = 16

    const STEP_DATA_KEYS = {
        1: 'aboutYou',
        2: 'whatHappened',
        3: 'howYouFelt',
        4: 'symptomsToday',
        5: 'healthScreening',
        6: 'treatmentReceived',
        7: 'treatingProviders',
        8: 'testsImaging',
        9: 'previousInjuries',
        10: 'medicalSurgical',
        11: 'medicationsAllergies',
        12: 'workInformation',
        13: 'socialFamily',
        14: 'dailyActivities',
        15: 'healthQuestions',
        16: 'reviewSubmit',
        17: 'confirmSubmit',
    }

    function createEmptyFormData(aboutYouPrefill) {
        return {
            aboutYou: aboutYouPrefill,
            whatHappened: undefined,
            howYouFelt: undefined,
            symptomsToday: undefined,
            healthScreening: undefined,
            treatmentReceived: undefined,
            treatingProviders: undefined,
            testsImaging: undefined,
            previousInjuries: undefined,
            medicalSurgical: undefined,
            medicationsAllergies: undefined,
            workInformation: undefined,
            socialFamily: undefined,
            dailyActivities: undefined,
            healthQuestions: undefined,
            reviewSubmit: undefined,
            confirmSubmit: undefined,
        }
    }

    export default {
        components: {
            AccidentIntakeAboutYouForm,
            AccidentIntakeWhatHappenedForm,
            AccidentIntakeHowYouFeltForm,
            AccidentIntakeSymptomsTodayForm,
            AccidentIntakeHealthScreeningForm,
            AccidentIntakeTreatmentReceivedForm,
            AccidentIntakeTreatingProvidersForm,
            AccidentIntakeTestsImagingForm,
            AccidentIntakePreviousInjuriesForm,
            AccidentIntakeMedicalSurgicalHistoryForm,
            AccidentIntakeMedicationsAllergiesForm,
            AccidentIntakeWorkInformationForm,
            AccidentIntakeSocialFamilyHistoryForm,
            AccidentIntakeDailyActivitiesForm,
            AccidentIntakeHealthQuestionsForm,
            AccidentIntakeReviewSubmitForm,
            AccidentIntakeConfirmSubmitForm,
        },

        props: {
            patientDetails: {
                type: Object,
                default: null,
            },
            patientId: {
                type: [String, Number],
                default: null,
            },
            authenticated: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                currentStep: 1,
                returnToStep: null,
                isSubmitting: false,
                isSubmitted: false,
                submitError: null,
                submittedSummaryText: '',
                submittedPdfFile: null,
                isSummaryCopied: false,
                formData: createEmptyFormData(this.buildAboutYouPrefill()),
            }
        },

        computed: {
            currentStepMeta() {
                return {
                    progress: this.$t(`pages.intake.step${this.currentStep}.progress`),
                    heading: this.$t(`pages.intake.step${this.currentStep}.heading`),
                    description: this.$t(`pages.intake.step${this.currentStep}.description`),
                }
            },

            stepProgress() {
                return Math.round((this.currentStep / TOTAL_STEPS) * 100)
            },

            copyButtonLabel() {
                return this.isSummaryCopied ? this.$t('pages.intake.success.copied') : this.$t('pages.intake.success.copy')
            },
        },

        methods: {
            buildAboutYouPrefill() {
                const patient = this.patientDetails
                if (!patient || !Object.keys(patient).length) {
                    return undefined
                }

                const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim()

                return {
                    informationProvider: 'patient',
                    fullName: fullName || undefined,
                    dateOfBirth: patient.dateOfBirth || patient.dob || undefined,
                    phone: patient.mobilePhone || patient.phone || undefined,
                    email: patient.email || undefined,
                }
            },

            onStepComplete(step, data) {
                this.formData[STEP_DATA_KEYS[step]] = data

                if (this.returnToStep) {
                    this.currentStep = this.returnToStep
                    this.returnToStep = null
                    return
                }

                if (step < LAST_IMPLEMENTED_STEP) {
                    this.currentStep = step + 1
                }
            },

            onStepBack(step, data) {
                this.formData[STEP_DATA_KEYS[step]] = data

                if (this.returnToStep) {
                    this.currentStep = this.returnToStep
                    this.returnToStep = null
                    return
                }

                this.currentStep = step - 1
            },

            onReviewEdit({ step, data }) {
                this.formData.reviewSubmit = data
                this.returnToStep = REVIEW_STEP
                this.currentStep = step
            },

            async onFinalSubmit(data) {
                this.formData.confirmSubmit = data
                this.isSubmitting = true
                this.isSubmitted = false
                this.submitError = null

                const patientId = Number(this.patientId || this.$route.params.id)
                const userId = Number(this.$auth.getUser().id)
                const fromPracticeId = Number(
                    this.$auth.getUser().practiceId || (this.$auth.getPractice() && this.$auth.getPractice().id),
                )
                const payload = buildIntakePayload(this.formData, {
                    patientId,
                    authenticated: true,
                })

                try {
                    if (!patientId || !userId || Number.isNaN(patientId) || Number.isNaN(userId)) {
                        throw new Error(this.$t('pages.intake.submit_error'))
                    }
                    if (!fromPracticeId || Number.isNaN(fromPracticeId)) {
                        throw new Error(this.$t('pages.intake.submit_error'))
                    }

                    // 1) PUT patient intake  2) POST /patient/documents/upload
                    const result = await IntakeAPI.submitAndUpload({
                        patientId,
                        userId,
                        fromPracticeId,
                        payload,
                    })

                    this.submittedSummaryText = result.summaryText || ''
                    this.submittedPdfFile = result.pdfFile || null
                    this.isSummaryCopied = false
                    this.isSubmitted = true
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                } catch (error) {
                    const responseMessage = Array.isArray(error?.response?.data)
                        ? error.response.data[0]?.message
                        : error?.response?.data?.message
                    this.submitError = responseMessage || error?.message || this.$t('pages.intake.submit_error')
                } finally {
                    this.isSubmitting = false
                }
            },

            async copySummaryToClipboard() {
                const text = this.submittedSummaryText
                if (!text) {
                    return
                }

                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(text)
                    } else {
                        const textarea = document.createElement('textarea')
                        textarea.value = text
                        textarea.setAttribute('readonly', '')
                        textarea.style.position = 'absolute'
                        textarea.style.left = '-9999px'
                        document.body.appendChild(textarea)
                        textarea.select()
                        document.execCommand('copy')
                        document.body.removeChild(textarea)
                    }
                    this.isSummaryCopied = true
                    window.setTimeout(() => {
                        this.isSummaryCopied = false
                    }, 2000)
                } catch (error) {
                    this.submitError = error?.message || this.$t('pages.intake.submit_error')
                }
            },

            downloadSummaryPdf() {
                const pdfFile = this.submittedPdfFile
                if (!pdfFile?.file) {
                    return
                }

                const link = document.createElement('a')
                link.href = pdfFile.file
                link.download = pdfFile.fileName || 'Intake Form.pdf'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            },

            resetIntake() {
                this.currentStep = 1
                this.returnToStep = null
                this.isSubmitting = false
                this.isSubmitted = false
                this.submitError = null
                this.submittedSummaryText = ''
                this.submittedPdfFile = null
                this.isSummaryCopied = false
                this.formData = createEmptyFormData(this.buildAboutYouPrefill())
                window.scrollTo({ top: 0, behavior: 'smooth' })
            },
        },

        watch: {
            currentStep() {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            },
            patientDetails: {
                deep: true,
                handler(patient) {
                    if (this.currentStep !== 1 || this.formData.aboutYou) {
                        return
                    }
                    if (patient && Object.keys(patient).length) {
                        this.formData.aboutYou = this.buildAboutYouPrefill()
                    }
                },
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .accident-intake-wizard {
        width: 100%;
    }

    .intake-page__step-label {
        margin: 0;
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-page__step-intro {
        margin: rem(20px) 0 rem(24px);
    }

    .intake-page__step-heading {
        ::v-deep .page-header__heading {
            margin: 0;
            line-height: 1.2;
        }
    }

    .intake-page__step-description {
        margin: rem(6px) 0 0;
        font-size: rem($font-size--hint);
        line-height: 1.4;
        color: $color--txt-base;
    }

    .intake-page__submit-error {
        margin: rem(16px) 0 0;
        font-size: rem($font-size--form);
        color: $color--accent-4;
    }

    .intake-submitted {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 100%;
    }

    .intake-submitted__heading {
        margin: 0;
        text-align: center;
        font-size: rem($font-size--heading);
        font-weight: 700;
        color: $color--txt-header;
    }

    .intake-submitted__message {
        margin: rem(12px) auto rem(24px);
        max-width: rem(640px);
        text-align: center;
        font-size: rem($font-size--form);
        line-height: 1.5;
        color: $color--txt-base;
    }

    .intake-submitted__actions {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        width: 100%;
        margin: 0 0 rem(20px);
        gap: rem(16px);
    }

    .intake-submitted__actions ::v-deep .button {
        flex: 0 0 auto;
        width: auto;
        margin: 0;
    }

    .intake-submitted__action {
        flex: 0 0 auto;
        margin: 0 !important;
    }

    .intake-submitted__summary-wrap {
        width: 100%;
        max-height: rem(420px);
        overflow: hidden;
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    }

    .intake-submitted__summary {
        margin: 0;
        padding: rem(20px);
        max-height: rem(420px);
        overflow-x: hidden;
        overflow-y: auto;
        text-align: left;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: Consolas, 'Courier New', monospace;
        font-size: rem(13px);
        line-height: 1.55;
        color: $color--txt-base;
        background: transparent;
        border: 0;
        box-sizing: border-box;
    }
</style>
